import { useEffect, useMemo, useState } from 'react'
import { COUNTRY_FLAGS, getClub } from '../game'

type Rgb = readonly [number, number, number]

export interface TeamPalette {
  primary: string
  secondary: string
}

export interface MatchTeamColors {
  home: string
  away: string
  homeSwatch: string
  awaySwatch: string
  awayUsesSecondary: boolean
}

const PAPER: Rgb = [232, 224, 205]
const paletteCache = new Map<string, Promise<TeamPalette>>()

const clampByte = (value: number) => Math.max(0, Math.min(255, Math.round(value)))
const toHex = ([red, green, blue]: Rgb) => `#${[red, green, blue].map((value) => clampByte(value).toString(16).padStart(2, '0')).join('')}`

function fromHex(color: string): Rgb {
  const value = color.replace('#', '')
  return [Number.parseInt(value.slice(0, 2), 16), Number.parseInt(value.slice(2, 4), 16), Number.parseInt(value.slice(4, 6), 16)]
}

function saturation([red, green, blue]: Rgb) {
  const [r, g, b] = [red, green, blue].map((value) => value / 255)
  const high = Math.max(r, g, b)
  const low = Math.min(r, g, b)
  const lightness = (high + low) / 2
  return high === low ? 0 : (high - low) / (1 - Math.abs(2 * lightness - 1))
}

function lightness([red, green, blue]: Rgb) {
  return (Math.max(red, green, blue) + Math.min(red, green, blue)) / 510
}

function channelLuminance(value: number) {
  const channel = value / 255
  return channel <= .04045 ? channel / 12.92 : ((channel + .055) / 1.055) ** 2.4
}

function luminance([red, green, blue]: Rgb) {
  return .2126 * channelLuminance(red) + .7152 * channelLuminance(green) + .0722 * channelLuminance(blue)
}

function contrast(first: Rgb, second: Rgb) {
  const bright = Math.max(luminance(first), luminance(second))
  const dark = Math.min(luminance(first), luminance(second))
  return (bright + .05) / (dark + .05)
}

function mix(first: Rgb, second: Rgb, amount: number): Rgb {
  return first.map((value, index) => value + (second[index]! - value) * amount) as unknown as Rgb
}

function readableOnPaper(color: string) {
  let rgb = fromHex(color)
  for (let step = 0; step < 12 && contrast(rgb, PAPER) < 4.5; step += 1) rgb = mix(rgb, [17, 31, 35], .13)
  return toHex(rgb)
}

export function colorDistance(first: string, second: string) {
  const a = fromHex(first)
  const b = fromHex(second)
  return Math.sqrt(a.reduce((sum, value, index) => sum + (value - b[index]!) ** 2, 0))
}

export function selectDistinctTeamColors(home: TeamPalette, away: TeamPalette): MatchTeamColors {
  const awayUsesSecondary = colorDistance(home.primary, away.primary) < 82
  const awayColor = awayUsesSecondary ? away.secondary : away.primary
  return {
    home: readableOnPaper(home.primary),
    away: readableOnPaper(awayColor),
    homeSwatch: home.primary,
    awaySwatch: awayColor,
    awayUsesSecondary,
  }
}

function fallbackPalette(name: string): TeamPalette {
  const hash = [...name].reduce((value, character) => ((value * 33) ^ character.charCodeAt(0)) >>> 0, 2166136261)
  const hue = hash % 360
  const secondaryHue = (hue + 145 + (hash % 55)) % 360
  return { primary: hslToHex(hue, 62, 39), secondary: hslToHex(secondaryHue, 58, 34) }
}

function hslToHex(hue: number, saturationValue: number, lightnessValue: number) {
  const saturation = saturationValue / 100
  const lightness = lightnessValue / 100
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation
  const section = hue / 60
  const x = chroma * (1 - Math.abs((section % 2) - 1))
  const [r, g, b] = section < 1 ? [chroma, x, 0] : section < 2 ? [x, chroma, 0] : section < 3 ? [0, chroma, x] : section < 4 ? [0, x, chroma] : section < 5 ? [x, 0, chroma] : [chroma, 0, x]
  const match = lightness - chroma / 2
  return toHex([(r + match) * 255, (g + match) * 255, (b + match) * 255])
}

function assetForTeam(name: string) {
  return COUNTRY_FLAGS[name] ?? getClub(name)?.crest ?? null
}

function paletteFromAsset(asset: string, fallback: TeamPalette): Promise<TeamPalette> {
  const cached = paletteCache.get(asset)
  if (cached) return cached
  const pending = new Promise<TeamPalette>((resolve) => {
    const image = new Image()
    image.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = 48
        canvas.height = 48
        const context = canvas.getContext('2d', { willReadFrequently: true })
        if (!context) return resolve(fallback)
        context.drawImage(image, 0, 0, 48, 48)
        const pixels = context.getImageData(0, 0, 48, 48).data
        const bins = new Map<string, { rgb: Rgb; score: number }>()
        for (let index = 0; index < pixels.length; index += 16) {
          const alpha = pixels[index + 3]!
          if (alpha < 96) continue
          const rgb: Rgb = [pixels[index]!, pixels[index + 1]!, pixels[index + 2]!]
          const sat = saturation(rgb)
          const lit = lightness(rgb)
          if (sat < .18 || lit < .09 || lit > .9) continue
          const quantized: Rgb = rgb.map((value) => Math.round(value / 32) * 32) as unknown as Rgb
          const key = quantized.join('-')
          const current = bins.get(key)
          bins.set(key, { rgb: quantized, score: (current?.score ?? 0) + .5 + sat })
        }
        const candidates = [...bins.values()].sort((left, right) => right.score - left.score)
        if (!candidates.length) return resolve(fallback)
        const primary = candidates[0]!.rgb
        const secondary = candidates.find((candidate) => colorDistance(toHex(primary), toHex(candidate.rgb)) >= 82)?.rgb ?? fromHex(fallback.secondary)
        resolve({ primary: toHex(primary), secondary: toHex(secondary) })
      } catch {
        resolve(fallback)
      }
    }
    image.onerror = () => resolve(fallback)
    image.src = asset
  })
  paletteCache.set(asset, pending)
  return pending
}

function useTeamPalette(name: string) {
  const fallback = useMemo(() => fallbackPalette(name), [name])
  const [palette, setPalette] = useState(fallback)
  useEffect(() => {
    let active = true
    const asset = assetForTeam(name)
    setPalette(fallback)
    if (asset) void paletteFromAsset(asset, fallback).then((next) => { if (active) setPalette(next) })
    return () => { active = false }
  }, [fallback, name])
  return palette
}

export function useMatchTeamColors(homeName: string, awayName: string) {
  const home = useTeamPalette(homeName)
  const away = useTeamPalette(awayName)
  return useMemo(() => selectDistinctTeamColors(home, away), [away, home])
}
